import { User, Sparkles, Copy, Check } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import Markdown from '@/components/markdown/Markdown';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';
import ToolProgressStepper from './ToolProgressStepper';
import useCopyState from './useCopyState';
import type { ChatMessage } from './useResearchChat';

interface MessageBubbleProps {
  message: ChatMessage;
  isStreaming?: boolean;
  /** All artifacts from the thread — used as fallback when message.artifacts
   *  is not populated (loaded threads vs streaming). */
  allArtifacts?: ResearchArtifact[];
}

function MessageBubble({ message, isStreaming, allArtifacts }: MessageBubbleProps) {
  const [copied, triggerCopied] = useCopyState();
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content).then(() => triggerCopied()).catch(() => {});
  }, [message.content, triggerCopied]);

  const isUser = message.role === 'user';

  // Build a respondent_index → { conversationId, messageId } map from
  // artifacts. During streaming, artifacts are attached to the message
  // directly. For loaded threads, message.artifacts is empty so we fall
  // back to allArtifacts (all artifacts in the thread). Respondent
  // indices are project-scoped so using all artifacts is safe.
  const respondentMap = useMemo(() => {
    const map = new Map<number, { conversationId: string; messageId?: string }>();
    const artifactsToScan = message.artifacts?.length
      ? message.artifacts
      : (allArtifacts || []);
    for (const artifact of artifactsToScan) {
      if (artifact.type !== 'quote_collection') continue;
      const quotes = artifact.data?.quotes || [];
      for (const q of quotes) {
        if (q.respondent_index != null && q.conversation_id) {
          map.set(Number(q.respondent_index), {
            conversationId: q.conversation_id,
            messageId: q.message_id,
          });
        }
      }
    }
    return map;
  }, [message.artifacts, allArtifacts]);

  // Parse [R{n}] citations and make them clickable.
  // When citations are present we render the surrounding text fragments
  // with isParagraphSpanEl=true so the Markdown component renders inline
  // <span>s instead of <p>/<ul> block elements (which would be invalid
  // HTML inside the wrapping span).
  const renderContent = (content: string) => {
    if (!content) return null;

    // Split on [R{n}] pattern
    const parts = content.split(/(\[R\d+\])/g);
    if (parts.length === 1) {
      return <Markdown>{content}</Markdown>;
    }

    return (
      <span className="leading-relaxed">
        {parts.map((part, i) => {
          const match = part.match(/^\[R(\d+)\]$/);
          if (match) {
            const respondentIdx = parseInt(match[1], 10);
            const ref = respondentMap.get(respondentIdx);
            const isClickable = !!ref;
            return (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                type="button"
                disabled={!isClickable}
                aria-disabled={!isClickable}
                className={cn(
                  // Visible pill stays compact (inline with text). Padding
                  // gives a slightly larger hit target without breaking the
                  // inline-text flow. WCAG 2.5.5 inline-target exception
                  // applies to citation pills like this.
                  'inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-1',
                  isClickable
                    ? 'bg-emerald-500 opacity-80 hover:opacity-100 cursor-pointer'
                    : 'bg-neutral-300 opacity-60 cursor-not-allowed',
                )}
                aria-label={
                  isClickable
                    ? `View transcript for Respondent ${match[1]}`
                    : `Respondent ${match[1]} (transcript not available in this view)`
                }
                title={
                  isClickable
                    ? `View Respondent ${match[1]}'s transcript`
                    : `Respondent ${match[1]}`
                }
                onClick={() => {
                  if (ref) openTranscriptPanel(ref.conversationId, ref.messageId);
                }}
              >
                {match[1]}
              </button>
            );
          }
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Markdown key={i} isParagraphSpanEl>
              {part}
            </Markdown>
          );
        })}
      </span>
    );
  };

  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="flex items-start gap-2.5 max-w-[80%]">
          <div className="bg-emerald-50 rounded-2xl rounded-tr-sm px-4 py-2.5">
            <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">
              {message.content}
            </p>
          </div>
          <div className="flex items-center justify-center h-7 w-7 rounded-full bg-emerald-100 shrink-0 mt-0.5">
            <User className="h-3.5 w-3.5 text-emerald-600" />
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex mb-4 group">
      <div className="flex items-start gap-2.5 max-w-[85%]">
        <div className="flex items-center justify-center h-7 w-7 rounded-full bg-neutral-100 shrink-0 mt-0.5">
          <Sparkles className="h-3.5 w-3.5 text-neutral-500" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Tool progress — archival "X tools used" line, only shown
              AFTER streaming finishes. While streaming is in progress
              the live progress stepper is rendered in a pinned bar at
              the bottom of ChatThread (above the input) so it stays
              visible regardless of the message bubble's scroll
              position. */}
          {!isStreaming && message.tool_steps && message.tool_steps.length > 0 && (
            <ToolProgressStepper steps={message.tool_steps} collapsed />
          )}

          {/* Message content */}
          {message.content && (
            <div
              className="text-sm text-neutral-800 leading-relaxed"
              aria-live={isStreaming ? 'polite' : undefined}
            >
              {renderContent(message.content)}
              {isStreaming && (
                <span className="inline-block w-1.5 h-4 bg-neutral-400 animate-pulse ml-0.5 align-text-bottom" />
              )}
            </div>
          )}

          {/* Artifact references */}
          {message.artifacts && message.artifacts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {message.artifacts.map((artifact) => (
                <span
                  key={artifact.artifact_id}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md"
                >
                  <Sparkles className="h-3 w-3" />
                  {artifact.title} added
                </span>
              ))}
            </div>
          )}

          {/* Actions — visible on hover OR keyboard focus.
              Sprint A audit fix: previously the wrapper was opacity-0 +
              group-hover:opacity-100 with no focus-within fallback, so a
              keyboard user who Tab'd to the (invisible) copy button saw
              nothing — a WCAG 2.4.7 focus-visible failure. Added
              focus-within:opacity-100 so the button reveals itself when
              keyboard focus reaches it. */}
          {!isStreaming && message.content && (
            <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={handleCopy}
                className={cn(
                  'p-2 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300',
                  copied ? 'text-emerald-500' : 'text-neutral-500',
                )}
                title={copied ? 'Copied' : 'Copy message'}
                aria-label={copied ? 'Message copied' : 'Copy message'}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageBubble;
